//App.js
// Updated: 2025-01-20 16:56:00 UTC+8
// Added: Website configuration system integration

import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/web/Header/Header';
import Footer from './components/web/Footer/footer';
import HomePage from './components/web/HomePage/HomePage'; // Keep home page for immediate loading
import 'bootstrap/dist/css/bootstrap.min.css';
import CookieConsent from './components/web/CookieConsent/CookieConsent';
import Analytics from './components/web/Analytics/Analytics';
import { I18nextProvider } from 'react-i18next';
import i18n from './components/web/i18n/i18n';
import { initPerformanceOptimizations } from './utils/performance';

// Configuration System Imports
import { ConfigProvider, useConfig } from './components/system/providers/ConfigProvider';
import { SEOHead } from './components/system/seo/SEOManager';

// Lazy load components for better performance
const News = lazy(() => import('./components/web/News/News'));
const PrivacyPolicy = lazy(() => import('./components/web/Privacy/privacy'));
const AboutusPage = lazy(() => import('./components/web/Aboutus/AboutusPage'));
const AI4GoodPage = lazy(() => import('./components/web/AI4Good/AI4GoodPage'));
const NewsEdit = lazy(() => import('./components/web/News/News_edit'));
const TermsOfUse = lazy(() => import('./components/web/TermsOfUse/TermsOfUse'));
const Exhibitors = lazy(() => import('./components/web/Exhibitors/Exhibitors'));
const AdminRoute = lazy(() => import('./components/system/admin/AdminRoute'));
const NotFound = lazy(() => import('./components/Others/NotFound'));
const ProjectPage = lazy(() => import('./components/web/Project/ProjectPage'));

// Loading component for Suspense
const LoadingSpinner = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
    <div className="spinner-border text-primary" role="status">
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught an error:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return <div style={{ padding: 24 }}>Something went wrong.</div>;
    }
    return this.props.children;
  }
}

/**
 * Main App Component with Configuration System Integration
 */
const AppContent = () => {
  const { config, loading } = useConfig();

  // Initialize performance optimizations
  useEffect(() => {
    initPerformanceOptimizations();
  }, []);

  

  // Show loading spinner while configuration is loading
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <I18nextProvider i18n={i18n}>
      <ErrorBoundary>
        <Router>
          <AppRoutesLayout config={config} />
        </Router>
      </ErrorBoundary>
    </I18nextProvider>
  );
};

function AppRoutesLayout({ config }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <>
      <SEOHead config={config} />
      <Analytics />
      <CookieConsent />
      <div className={'App'}>
        <Header />
        <div className={config?.features?.blackWhiteMode && !isAdminRoute ? 'bw-content' : ''}>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route exact path="/" element={<HomePage />} />
              <Route path="/news" element={<News key={i18n.language} />} />
              <Route path="/newsedit" element={<NewsEdit />} />
              <Route path="/Exhibitors" element={<Exhibitors key={i18n.language} />} />
              <Route path="/aboutus" element={<AboutusPage />} />
              <Route path="/AI4Good" element={<AI4GoodPage key={i18n.language} />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy key={i18n.language} />} />
              <Route path="/TermsOfUse" element={<TermsOfUse />} />
              <Route path="/project" element={<ProjectPage />} />
              <Route path="/admin" element={<AdminRoute />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <Footer key={i18n.language} />
        </div>
      </div>
    </>
  );
}

/**
 * Root App Component with Configuration Provider
 */
function App() {
  return (
    <ConfigProvider>
      <AppContent />
    </ConfigProvider>
  );
}

export default App;
